import React, { useEffect, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import styles from "../assets/css/adminLayout.module.css";

const BaseURL = process.env.REACT_APP_BASEURL;

const GMC = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [brand, setBrand] = useState("");
    const [selectedItems, setSelectedItems] = useState({}); // Stores { id: item }
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    // Fetch data from the API
    const fetchData = async () => {
        setLoading(true);
        try {
            // Construct URL with query parameters
            let url = `${BaseURL}/product/improved-product/generic_search/?isGMC=true&page=${currentPage}&page_size=${pageSize}`;
            if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
            if (minPrice) url += `&min_price=${minPrice}`;
            if (maxPrice) url += `&max_price=${maxPrice}`;
            if (brand) url += `&brand=${encodeURIComponent(brand)}`;

            const response = await axios.get(url, {
                headers: {
                    accept: "application/json",
                    Authorization: `Token ${localStorage.getItem("adminAuth")}`,
                },
            });

            if (response.data) {
                const fetchedData = response.data.results || response.data;
                if (Array.isArray(fetchedData)) {
                    setData(fetchedData);
                    setTotalPages(response.data.total_pages || 1);
                } else {
                    setData([]);
                    setTotalPages(1);
                }
            } else {
                setData([]);
                setTotalPages(1);
            }
        } catch (error) {
            console.error("Error fetching GMC data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchData();
        }, 500); // Debounce search

        return () => clearTimeout(delayDebounceFn);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, minPrice, maxPrice, brand, currentPage, pageSize]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset to page 1 on search
    };

    const handleSelectAll = (e) => {
        const newSelectedItems = { ...selectedItems };

        // Check if all currently visible items are already selected
        const allVisibleSelected = data.length > 0 && data.every(item => !!selectedItems[item.Id]);

        if (allVisibleSelected) {
            // Deselect all visible items
            data.forEach(item => {
                delete newSelectedItems[item.Id];
            });
        } else {
            // Select all visible items
            data.forEach(item => {
                newSelectedItems[item.Id] = item;
            });
        }
        setSelectedItems(newSelectedItems);
    };

    const handleSelectRow = (item) => {
        const newSelectedItems = { ...selectedItems };
        if (newSelectedItems[item.Id]) {
            delete newSelectedItems[item.Id];
        } else {
            newSelectedItems[item.Id] = item;
        }
        setSelectedItems(newSelectedItems);
    };

    const exportToExcel = (dataToExport, fileName) => {
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
    };

    const handleDownloadAll = () => {
        exportToExcel(data, "GMC_All_Data");
    };

    const handleDownloadSelected = () => {
        const itemsToExport = Object.values(selectedItems);
        if (itemsToExport.length === 0) {
            alert("No items selected!");
            return;
        }
        exportToExcel(itemsToExport, "GMC_Selected_Data");
    };

    return (
        <div className={styles.container} style={{ padding: "20px" }}>
            <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                    <h2>GMC Data</h2>
                    <div style={{ display: "flex", gap: "10px" }}>
                        <button onClick={handleDownloadAll} style={buttonStyle}>
                            Download All
                        </button>
                        <button onClick={handleDownloadSelected} style={buttonStyle}>
                            Download Selected ({Object.keys(selectedItems).length})
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        style={inputStyle}
                    />
                    <input
                        type="text"
                        placeholder="Brand"
                        value={brand}
                        onChange={(e) => { setBrand(e.target.value); setCurrentPage(1); }}
                        style={inputStyle}
                    />
                    <input
                        type="number"
                        placeholder="Min Price"
                        value={minPrice}
                        onChange={(e) => { setMinPrice(e.target.value); setCurrentPage(1); }}
                        style={inputStyle}
                    />
                    <input
                        type="number"
                        placeholder="Max Price"
                        value={maxPrice}
                        onChange={(e) => { setMaxPrice(e.target.value); setCurrentPage(1); }}
                        style={inputStyle}
                    />
                </div>
            </div>

            {loading ? (
                <p>Loading...</p>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", backgroundColor: "#fff" }}>
                        <thead>
                            <tr style={{ backgroundColor: "#019c89", color: "white", textAlign: "left" }}>
                                <th style={thStyle}>
                                    <input
                                        type="checkbox"
                                        checked={data.length > 0 && data.every(item => !!selectedItems[item.Id])}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th style={thStyle}>ID</th>
                                <th style={thStyle}>Image</th>
                                <th style={thStyle}>Title</th>
                                <th style={thStyle}>Brand</th>
                                <th style={thStyle}>Price</th>
                                <th style={thStyle}>Condition</th>
                                <th style={thStyle}>Availability</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.length > 0 ? (
                                data.map((item) => (
                                    <tr key={item.Id} style={{ borderBottom: "1px solid #ddd" }}>
                                        <td style={tdStyle}>
                                            <input
                                                type="checkbox"
                                                checked={!!selectedItems[item.Id]}
                                                onChange={() => handleSelectRow(item)}
                                            />
                                        </td>
                                        <td style={tdStyle}>{item.Id}</td>
                                        <td style={tdStyle}>
                                            <img src={item.Image_Link} alt={item.Title} style={{ width: "50px", height: "50px", objectFit: "cover" }} />
                                        </td>
                                        <td style={tdStyle}>
                                            <a href={item.Link} target="_blank" rel="noreferrer" style={{ color: '#019c89', textDecoration: 'none' }}>
                                                {item.Title}
                                            </a>
                                        </td>
                                        <td style={tdStyle}>{item.Brand}</td>
                                        <td style={tdStyle}>{item.Price}</td>
                                        <td style={tdStyle}>{item.Condition}</td>
                                        <td style={tdStyle}>{item.Availability}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" style={{ padding: "20px", textAlign: "center" }}>
                                        No data found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination Controls */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px", alignItems: "center", gap: "10px" }}>
                <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    style={{ ...buttonStyle, backgroundColor: currentPage === 1 ? "#ccc" : "#019c89", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                >
                    Previous
                </button>

                <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    style={{ ...inputStyle, minWidth: "auto" }}
                >
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                </select>

                <span>
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    style={{ ...buttonStyle, backgroundColor: currentPage === totalPages ? "#ccc" : "#019c89", cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
                >
                    Next
                </button>
            </div>
        </div>
    );
};

const buttonStyle = {
    backgroundColor: "#019c89",
    color: "white",
    border: "none",
    padding: "8px 15px",
    borderRadius: "5px",
    cursor: "pointer",
};

const inputStyle = {
    padding: "8px",
    borderRadius: "5px",
    border: "1px solid #ccc",
    minWidth: "150px",
};

const thStyle = {
    padding: "12px",
    borderBottom: "1px solid #ddd",
};

const tdStyle = {
    padding: "12px",
};

export default GMC;
